import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, ExternalLink } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { FadeUp } from "@/components/site/Reveal";
import { PROJECTS } from "@/data/site";
import { usePageMeta } from "@/hooks/usePageMeta";

const InfoBlock = ({ label, value }) => (
  <div className="glass rounded-2xl p-5">
    <p className="font-mono-label text-[9px] tracking-[0.25em] uppercase text-[var(--text-muted)] mb-2">{label}</p>
    <p className="text-sm md:text-base text-white/90 font-medium">{value}</p>
  </div>
);

export default function Projet() {
  const { slug } = useParams();
  const project = PROJECTS.find((p) => p.slug === slug);
  const others = PROJECTS.filter((p) => p.slug !== slug).slice(0, 3);

  usePageMeta({ title: project?.seoTitle, description: project?.seoDescription });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!project) return <Navigate to="/" replace />;

  return (
    <div className="relative min-h-screen bg-[var(--bg-primary)]" data-testid="projet-page">
      <Header />
      <main className="relative pt-32 pb-24 overflow-hidden">
        <div className="glow-orb w-[520px] h-[520px] bg-blue-600/14 -top-32 -left-40" />
        <div className="glow-orb w-[420px] h-[420px] bg-violet-600/12 top-[40%] -right-40" />

        <div className="max-w-6xl mx-auto px-6 md:px-10 relative">
          <FadeUp>
            <Link
              to="/#realisations"
              className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-white transition-colors mb-10"
              data-testid="projet-back-link"
            >
              <ArrowLeft size={15} /> Toutes les réalisations
            </Link>
          </FadeUp>

          <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 items-end mb-14">
            <div>
              <FadeUp>
                <p className="section-label mb-5">— {project.category}</p>
              </FadeUp>
              <FadeUp delay={0.08}>
                <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.04]" data-testid="projet-title">
                  {project.shortName} <span className="text-gradient">.</span>
                </h1>
              </FadeUp>
              <FadeUp delay={0.16}>
                <p className="text-[var(--text-secondary)] text-base md:text-lg mt-5 leading-relaxed max-w-xl">{project.description}</p>
              </FadeUp>
            </div>
            <FadeUp delay={0.2}>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gradient inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white"
                  data-testid="projet-visit-live"
                >
                  Visiter le site en ligne <ExternalLink size={14} />
                </a>
                <Link
                  to="/demande"
                  className="glass inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm text-white/90 hover:bg-white/[0.07] transition-colors"
                  data-testid="projet-cta-apercu"
                >
                  Un site comme celui-ci ? <ArrowRight size={14} />
                </Link>
              </div>
            </FadeUp>
          </div>

          <motion.a
            href={project.url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="block rounded-3xl overflow-hidden border border-white/[0.1] group"
            data-testid="projet-main-capture"
          >
            <img
              src={project.image}
              alt={project.alt}
              className="w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.015]"
            />
          </motion.a>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            <FadeUp><InfoBlock label="Secteur" value={project.category} /></FadeUp>
            <FadeUp delay={0.06}><InfoBlock label="Lieu" value={project.location} /></FadeUp>
            <FadeUp delay={0.12}><InfoBlock label="Type de site" value={project.siteType} /></FadeUp>
            <FadeUp delay={0.18}><InfoBlock label="Réalisé par" value="Les Amis du Web" /></FadeUp>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 mt-20 md:mt-28">
            <FadeUp>
              <p className="section-label mb-5">— Le projet</p>
              <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight leading-snug">
                Un univers pensé pour <span className="text-gradient">cette activité.</span>
              </h2>
              <p className="text-[var(--text-secondary)] text-base md:text-lg mt-5 leading-relaxed">{project.story}</p>
              <p className="text-[var(--text-secondary)] text-base md:text-lg mt-4 leading-relaxed">{project.approach}</p>
            </FadeUp>
            <FadeUp delay={0.12}>
              <div className="card-surface rounded-3xl p-8">
                <p className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[var(--accent-cyan)] mb-6">
                  Fonctionnalités clés
                </p>
                <ul className="flex flex-col gap-4">
                  {project.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm md:text-base text-white/90">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-blue-500/25 to-cyan-400/25 border border-cyan-400/30 flex items-center justify-center">
                        <Check size={12} className="text-[var(--accent-cyan)]" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
          </div>

          <div className="mt-20 md:mt-28">
            <FadeUp>
              <p className="section-label mb-5">— À l'intérieur du site</p>
              <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-10">
                Quelques <span className="text-gradient">captures supplémentaires.</span>
              </h2>
            </FadeUp>
            <div className="grid md:grid-cols-2 gap-6">
              {project.gallery.map((src, i) => (
                <motion.div
                  key={src}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  className="rounded-2xl overflow-hidden border border-white/[0.09] group"
                  data-testid={`projet-gallery-${i}`}
                >
                  <img
                    src={src}
                    alt={`${project.shortName} — page intérieure du site (capture ${i + 1})`}
                    className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                    loading="lazy"
                  />
                </motion.div>
              ))}
            </div>
          </div>

          <FadeUp className="mt-20 md:mt-28">
            <div className="relative rounded-3xl border border-white/[0.09] bg-gradient-to-r from-blue-600/[0.1] via-violet-600/[0.08] to-transparent p-8 md:p-12 overflow-hidden">
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-cyan-500/10 blur-[90px]" />
              <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                    Votre activité mérite le même <span className="text-gradient">soin.</span>
                  </h2>
                  <p className="text-[var(--text-secondary)] mt-3 max-w-xl leading-relaxed">
                    Décrivez-nous votre projet : nous créons gratuitement un aperçu de votre futur site internet,
                    adapté à votre univers. Sans engagement. En savoir plus sur{" "}
                    <Link to="/creation-site-internet" className="text-[var(--accent-cyan)] hover:underline">
                      la création de site internet sur mesure
                    </Link>
                    .
                  </p>
                </div>
                <Link
                  to="/demande"
                  className="btn-gradient inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 font-semibold text-white flex-shrink-0"
                  data-testid="projet-cta-final"
                >
                  Demander mon aperçu gratuit <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </FadeUp>

          <div className="mt-20 md:mt-28">
            <FadeUp>
              <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-10">
                D'autres <span className="text-gradient">réalisations.</span>
              </h2>
            </FadeUp>
            <div className="grid sm:grid-cols-3 gap-5">
              {others.map((p, i) => (
                <motion.div
                  key={p.slug}
                  initial={{ opacity: 0, y: 32 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={`/realisations/${p.slug}`}
                    className="group block rounded-2xl overflow-hidden border border-white/[0.08] bg-[#0b0c1c]"
                    data-testid={`projet-other-${p.slug}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.name}
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.05]"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5">
                      <p className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-[var(--accent-cyan)] mb-1.5">{p.category}</p>
                      <p className="font-display font-bold text-base flex items-center gap-1.5">
                        {p.shortName}
                        <ArrowUpRight size={14} className="text-[var(--text-muted)] group-hover:text-[var(--accent-cyan)] transition-colors" />
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
